import { isLayoutDesktop } from "@utils/isDesktop";
import { applyUTMParamsForLoginButtons } from "@utils/UTMParamUtils";
import debounce from "lodash/debounce";

export class CtaUtmsContainer {
  private el: HTMLElement;
  private breadcrumb: HTMLElement | null;
  private breadcrumbItemsList: HTMLElement | null;
  private lastBreadcrumbWidth: number | null = null;
  private lastBreadcrumbItemsCount: number | null = null;
  private lastTruncateIndex: number | null = null;
  private boundResizeHandler: (() => void) | null = null;

  constructor(el: HTMLElement) {
    this.el = el;
    this.breadcrumb = el.querySelector(".comp-enhanced-breadcrumb");
    this.breadcrumbItemsList = this.breadcrumb?.querySelector("ul") ?? null;
  }

  init(): void {
    applyUTMParamsForLoginButtons(this.el);

    if (this.breadcrumb && this.breadcrumbItemsList) {
      this.boundResizeHandler = debounce(() => {
        isLayoutDesktop() ? this.checkIfBreadcrumbFits() : this.mobileReset();
      }, 100);

      window.addEventListener("resize", this.boundResizeHandler);

      // Initial check
      isLayoutDesktop() ? this.checkIfBreadcrumbFits() : this.mobileReset();
    }
  }

  destroy(): void {
    if (this.boundResizeHandler) {
      (this.boundResizeHandler as ReturnType<typeof debounce>).cancel();
      window.removeEventListener("resize", this.boundResizeHandler);
      this.boundResizeHandler = null;
    }
  }

  private mobileReset(): void {
    this.resetBreadcrumbTruncationState();
  }

  private resetBreadcrumbTruncationState(): void {
    if (!this.breadcrumbItemsList) return;
    const items = this.breadcrumbItemsList.querySelectorAll("li");
    items.forEach((li) => {
      li.classList.remove("hidden-visually", "comp-enhanced-breadcrumb-item--truncated");
      const a = li.querySelector("a") as HTMLAnchorElement | null;
      if (a) {
        a.style.width = "auto";
        a.removeAttribute("data-universal-tooltip");
      }
    });
    this.lastBreadcrumbWidth = null;
    this.lastBreadcrumbItemsCount = null;
    this.lastTruncateIndex = null;
  }

  checkIfBreadcrumbFits(): void {
    if (!this.breadcrumb || !this.breadcrumbItemsList) return;

    const breadcrumbWidth = this.breadcrumb.getBoundingClientRect().width;
    const items = Array.from(this.breadcrumbItemsList.querySelectorAll("li"));
    const itemsCount = items.length;
    const MIN_TRUNCATED_WIDTH = 40;

    // Early exit: identical width & item count
    if (
      this.lastBreadcrumbWidth !== null &&
      this.lastBreadcrumbItemsCount !== null &&
      breadcrumbWidth === this.lastBreadcrumbWidth &&
      itemsCount === this.lastBreadcrumbItemsCount
    ) {
      return;
    }

    this.lastBreadcrumbWidth = breadcrumbWidth;
    this.lastBreadcrumbItemsCount = itemsCount;

    // Reset visual state for accurate measurement
    this.resetBreadcrumbTruncationState();

    let accumulatedWidth = 0;
    let truncateIndex = -1;

    for (let i = 0; i < items.length; i++) {
      accumulatedWidth += this.getOuterWidth(items[i]);
      if (accumulatedWidth > breadcrumbWidth) {
        truncateIndex = i;
        break;
      }
    }

    if (truncateIndex === -1) {
      this.lastTruncateIndex = null;
      return;
    }

    // Determine width before truncated item
    let widthBefore = 0;
    for (let i = 0; i < truncateIndex; i++) {
      widthBefore += this.getOuterWidth(items[i]);
    }
    let remaining = breadcrumbWidth - widthBefore;

    if (remaining < MIN_TRUNCATED_WIDTH && truncateIndex > 0) {
      // Hide from the original overflow item onwards
      items.slice(truncateIndex).forEach((li) => li.classList.add("hidden-visually"));
      truncateIndex = truncateIndex - 1;
      widthBefore = 0;
      for (let i = 0; i < truncateIndex; i++) {
        widthBefore += this.getOuterWidth(items[i]);
      }
      remaining = breadcrumbWidth - widthBefore;
    } else {
      items.slice(truncateIndex + 1).forEach((li) => li.classList.add("hidden-visually"));
    }

    const truncateItem = items[truncateIndex];
    if (!truncateItem) return;

    truncateItem.classList.add("comp-enhanced-breadcrumb-item--truncated");
    const a = truncateItem.querySelector("a") as HTMLAnchorElement | null;
    if (a) {
      a.style.width = Math.ceil(remaining) + "px";
      a.setAttribute("data-universal-tooltip", a.textContent ?? "");
    }
    this.lastTruncateIndex = truncateIndex;
  }

  private getOuterWidth(el: HTMLElement): number {
    const rect = el.getBoundingClientRect();
    const style = getComputedStyle(el);
    const marginLeft = parseFloat(style.marginLeft) || 0;
    const marginRight = parseFloat(style.marginRight) || 0;
    return rect.width + marginLeft + marginRight;
  }

  static initAll(root: Document | HTMLElement = document): void {
    root
      .querySelectorAll<HTMLElement>('[data-component="cta-utms-container"]')
      .forEach((el) => {
        new CtaUtmsContainer(el).init();
      });
  }
}
